
export default {
  name: "ProductPartsList",

  props: {
    id: { type: [String, Number], required: true },
    // top-level table (product → components) vs a nested drill-down level
    root: { type: Boolean, default: false },
    // selected configuration options for THIS level so its BOM reflects the configured model
    // rather than the INVENDET defaults; [{ category, id, quan, childs? }, ...]. The root gets
    // the full selection tree (ProductPage.getPartsListConfigurations); each expansion receives
    // the matching node's childs (see getChildConfigurations).
    configurations: { type: Array, default: () => [] },
    // the configurator's own tree for this level (product.configurations, or the matching item's
    // nested.configurations below the root) — what a configured row is added to the cart with
    tree: { type: Array, default: () => [] },
    // the product whose parts these are, for the base-kit warning
    productName: { type: String, default: "" }
  },

  data() {
    return {
      parts: [],
      loading: true,
      expandedRows: [],
      quantities: {},
      adding: {},
      reloadTimer: null,
      reqToken: 0
    }
  },

  watch: {
    id() {
      this.reload()
    },
    // re-fetch whenever the user changes an option (e.g. Standard → Signature benches).
    // The parent recomputes this prop on any config mutation (incl. config load after an offer
    // switch), so skip when the effective selection is unchanged — avoids a redundant refetch.
    configurations: {
      deep: true,
      handler(next, prev) {
        if (JSON.stringify(next) === JSON.stringify(prev)) return
        this.reload()
      }
    }
  },

  methods: {
    reload() {
      // the tree changed (variant or configuration) — drop stale expansions and refetch (coalesced)
      this.expandedRows = []
      clearTimeout(this.reloadTimer)
      this.reloadTimer = setTimeout(() => this.getParts(), 150)
    },

    async getParts() {
      this.loading = true

      const params = { id: this.id }
      // every level is configuration-aware: the root sends the full selection tree, an expansion
      // sends only the sub-selections that belong to its component (getChildConfigurations)
      if (this.configurations.length) {
        params.configurations = JSON.stringify(this.configurations)
      }

      // guard against out-of-order responses: only the latest request may write state
      const token = ++this.reqToken

      try {
        const parts = await this.$axios.$get("parts-list", { params })
        if (token !== this.reqToken) return
        this.parts = parts
      } catch (err) {
        if (token !== this.reqToken) return
        this.__logError(err)
        this.parts = []
      }

      this.loading = false
    },

    // Sub-selections for an expanded component: find its node anywhere in this level's selection
    // tree (it may sit below skipped non-web-visible wrappers — e.g. the visible heater HOMREVN
    // lives under the hidden "Electric Heater" wrapper) and hand its childs to the nested table.
    // Rows that were never configured (plain components) get none → default parts, as before.
    getChildConfigurations(id) {
      const find = (nodes) => {
        for (const node of nodes) {
          if (node.id === id) return node.childs || []

          if (node.childs?.length) {
            const found = find(node.childs)
            if (found) return found
          }
        }

        return null
      }

      return find(this.configurations) || []
    },

    // The configurator item a row stands for, searched the same way as getChildConfigurations
    // (through hidden wrappers), or null for a plain default part.
    getTreeItem(id) {
      const find = (configurations) => {
        for (const configuration of configurations) {
          if (configuration.type === "custom") continue // client-only Accessories

          const active = configuration.items?.find((item) => item.active)
          if (!active || active.id === -1) continue

          if (String(active.id) === String(id)) return active

          if (active.nested?.configurations?.length) {
            const found = find(active.nested.configurations)
            if (found) return found
          }
        }

        return null
      }

      return find(this.tree)
    },

    // The row's own options as picked in the configurator (e.g. the HOMREVN heater's size and
    // control) — empty when the row has none, so it goes to the cart as a plain part.
    getChildTree(id) {
      return this.getTreeItem(id)?.nested?.configurations || []
    },

    // The configurator's "Please select all required options" rule, along the picked path only.
    hasUncheckedRequired(configurations) {
      return configurations.some((configuration) => {
        const active = configuration.items?.find((item) => item.active && !item.isLoading)
        if (!active) return !configuration.allownone

        return !!active.nested?.configurations?.length && this.hasUncheckedRequired(active.nested.configurations)
      })
    },

    getBaseKitWarning(row) {
      const product = this.productName ? `the complete ${this.productName}` : "the complete product"

      return (
        `${row.id} is the base kit only, not ${product}: none of the configured options are included. ` +
        `To order the complete product, use “Add to cart” at the top of the page.`
      )
    },

    onAddClick(event, row) {
      if (!row.isBaseKit) return this.addToCart(row)

      const target = event.currentTarget

      this.$confirm.close()
      this.$nextTick(() => {
        this.$confirm.require({
          group: "parts-list",
          target,
          message: this.getBaseKitWarning(row),
          icon: "pi pi-exclamation-triangle",
          acceptLabel: "Add base kit",
          rejectLabel: "Cancel",
          accept: () => this.addToCart(row)
        })
      })
    },

    isExpanded(row) {
      return this.expandedRows.some((r) => r.id === row.id)
    },

    toggleRow(row) {
      if (this.isExpanded(row)) {
        this.expandedRows = this.expandedRows.filter((r) => r.id !== row.id)
      } else {
        this.expandedRows = [...this.expandedRows, row]
      }
    },

    getMax(row) {
      // cap at available stock when in stock; -1 = unlimited (made-to-order / no stock on hand)
      return Number(row.count) > 0 ? Number(row.count) : -1
    },

    setQuantity(id, quantity) {
      this.$set(this.quantities, id, quantity)
    },

    isQuantityValid(row) {
      const quantity = this.quantities[row.id] ?? 1
      const max = this.getMax(row)

      return quantity > 0 && (max === -1 || quantity <= max)
    },

    async addToCart(row) {
      // A configured row goes in as configured — sent the way the configurator sends a product,
      // so EBMS gets the dealer's picks instead of filling the item's defaults back in.
      const configured = this.getChildTree(row.id)

      if (this.hasUncheckedRequired(configured)) {
        this.__toastError(`Please select all required options for ${row.name} above first`)
        return
      }

      this.$set(this.adding, row.id, true)

      try {
        await this.$store.dispatch("CART/ADD", {
          id: row.id,
          unit: row.defUnit,
          quantity: this.quantities[row.id] ?? 1,
          configurations: this._getServerFormatedConfigurations(configured),
          formatedConfigurations: this._getCleanConfigurations(configured)
        })
        this.__toast("The product has been added to your cart")
      } catch (err) {
        this.__toastError(err?.source === "server" ? err.message : "")
      }

      this.$set(this.adding, row.id, false)
    }
  },

  created() {
    this.getParts()
  },

  beforeDestroy() {
    clearTimeout(this.reloadTimer)
  }
}
